import { z } from 'zod';
import {
  contactPersonSchema,
  createModel,
  imageWithUploadSchema,
  partyRoleSchema,
  QueryMany,
  QueryOne,
} from '../../common';
import { domainSchema, idSchema, nameSchema, objectIdSchema } from '../../validation';
import { profileSourceSchema, profileStatusSchema } from './profile.enums';

/**
 * `Profile db model` — global admin-curated brand directory entry.
 *
 * Any Org can READ Profile (search by alias / name) when creating a
 * Client; writes are admin-only (ADR #10 in
 * `docs/relations-v2/01-decisions.md`).
 *
 * Linked to a real Org once the brand signs up (`linkedOrgId`). Until
 * then the entry is a "ghost" brand record that any tenant Org can
 * point at from their CRM via `Client.profile = { _id, target: 'profile', ... }`.
 * When the brand later signs up, an admin sets `linkedOrgId`; a
 * background job mirrors that onto every Client doc referencing the
 * Profile.
 *
 * Expected indexes (operator-applied):
 * - `{ status: 1, name: 1 }` — list / typeahead
 * - `{ aliases: 1 }` — alternate-name lookups
 * - `{ registrationId: 1 }` (sparse) — registration-id match for signup linking
 * - `{ 'contact.phone': 1 }` (sparse), `{ 'contact.email': 1 }` (sparse)
 * - `{ linkedOrgId: 1 }` (sparse) — find profiles already mapped to an Org
 *
 * See `docs/relations-v2/02-data-model.md` section 7.
 */
export const ProfileSchema = createModel({
  /** display name (e.g. "Acme Logistics", "أرامكس"). */
  name: nameSchema,
  /** full legal name as registered. */
  legalName: nameSchema.optional(),
  /** commercial registration / VAT id where applicable. */
  registrationId: idSchema.optional(),

  /** company tax id */
  taxId: idSchema.optional(),

  /** alternate spellings, abbreviations, brand aliases. Searchable. */
  aliases: z.array(nameSchema).default([]),

  logo: imageWithUploadSchema.optional(),

  /** the domain such as tryhero.io or amazon.com etc.*/
  domain: domainSchema.optional(),

  /** best-known primary contact for the brand. */
  contact: contactPersonSchema.optional(),

  /**
   * if the brand has signed up as an Org, this points at it. Set by an
   * admin (or by the future signup-linking job) once the brand registers.
   */
  linkedOrgId: objectIdSchema.optional(),

  /** the network roles this brand can play. ≥ 1. */
  roles: z.array(partyRoleSchema).min(1),

  /** provenance — admin-curated, csv-imported, or future "suggested". */
  source: profileSourceSchema.default('admin'),
  /** lifecycle status. */
  status: profileStatusSchema.default('active'),
});

/** `Profile db model` — global admin-curated brand directory entry. */
export type Profile = z.infer<typeof ProfileSchema.model>;
export type ProfileData = z.infer<typeof ProfileSchema.data>;
export type ProfilePartial = z.infer<typeof ProfileSchema.partial>;
export type ProfileQueryMany = QueryMany<Profile>;
export type ProfileQueryOne = QueryOne<Profile>;
